import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'

export type GalleryItem = {
  id: string
  name: string
  thumbnail_url?: string
  image_url?: string
  group?: string
  gender?: string
  age?: string
  ethnicity?: string
  hair_color?: string
  description?: string
  figure_type?: string
  height_range?: string
  pose_name?: string
  body_part?: string
}

interface GalleryModalProps {
  open: boolean
  onClose: () => void
  onSelect: (item: GalleryItem) => void
  items: GalleryItem[]
  loading: boolean
  title: string
  defaultGroup?: string | null
  defaultGender?: string | null
  defaultAge?: string | null
  defaultBodyPart?: string | null
  groupLabel?: string
  imageObjectFit?: 'cover' | 'contain'
  imageHeight?: number
  imageAspectRatio?: string
}

const GalleryModal: React.FC<GalleryModalProps> = ({
  open, onClose, onSelect, items, loading, title,
  defaultGroup = null, defaultGender = null, defaultAge = null, defaultBodyPart = null,
  groupLabel = 'Kategoria:',
  imageObjectFit = 'cover',
  imageHeight = 120,
  imageAspectRatio,
}) => {
  const [search, setSearch] = useState('')
  const [selectedGroup, setSelectedGroup] = useState<string | null>(defaultGroup)
  const [selectedGender, setSelectedGender] = useState<string | null>(defaultGender)
  const [selectedHairColor, setSelectedHairColor] = useState<string | null>(null)
  const [selectedAge, setSelectedAge] = useState<string | null>(defaultAge)
  const [selectedBodyPart, setSelectedBodyPart] = useState<string | null>(defaultBodyPart)
  const prevOpenRef = useRef(false)
  const defaultsAppliedRef = useRef(false)

  // Apply defaults when modal opens — handles async item loading (items may arrive after open=true)
  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = open
    if (!open) {
      defaultsAppliedRef.current = false
      return
    }
    if (!wasOpen) {
      // Just opened — reset applied flag
      defaultsAppliedRef.current = false
    }
    if (!defaultsAppliedRef.current && items.length > 0) {
      defaultsAppliedRef.current = true
      const hasDefaultGroup = defaultGroup && items.some(item =>
        (item.ethnicity === defaultGroup) || (item.group === defaultGroup)
      )
      setSelectedGroup(hasDefaultGroup ? defaultGroup : null)
      setSelectedGender(defaultGender)
      setSelectedHairColor(null)
      setSelectedAge(defaultAge)
      setSelectedBodyPart(defaultBodyPart)
      setSearch('')
    }
  }, [open, items, defaultGroup, defaultGender, defaultAge, defaultBodyPart])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  const groups = useMemo(() => {
    const set = new Set<string>()
    items.forEach(item => { const val = item.ethnicity || item.group; if (val) set.add(val) })
    return Array.from(set).sort()
  }, [items])

  const genders = useMemo(() => {
    const set = new Set<string>()
    items.forEach(item => { if (item.gender) set.add(item.gender) })
    return Array.from(set).sort()
  }, [items])

  const ages = useMemo(() => {
    const set = new Set<string>()
    items.forEach(item => { if (item.age) set.add(item.age) })
    return Array.from(set).sort()
  }, [items])

  const hairColors = useMemo(() => {
    const set = new Set<string>()
    items.forEach(item => { if (item.hair_color) set.add(item.hair_color) })
    return Array.from(set).sort()
  }, [items])

  const bodyParts = useMemo(() => {
    const set = new Set<string>()
    items.forEach(item => { if (item.body_part) set.add(item.body_part) })
    return Array.from(set).sort()
  }, [items])

  const filtered = useMemo(() => {
    let result = items
    if (selectedGroup)
      result = result.filter(item => (item.ethnicity === selectedGroup) || (item.group === selectedGroup))
    if (selectedGender)
      result = result.filter(item => item.gender === selectedGender)
    if (selectedAge)
      result = result.filter(item => item.age === selectedAge)
    if (selectedHairColor)
      result = result.filter(item => item.hair_color === selectedHairColor)
    if (selectedBodyPart)
      result = result.filter(item => item.body_part === selectedBodyPart)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter(item =>
        item.name.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q) ||
        (item.pose_name && item.pose_name.toLowerCase().includes(q)) ||
        (item.figure_type && item.figure_type.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (item.group && item.group.toLowerCase().includes(q)) ||
        (item.ethnicity && item.ethnicity.toLowerCase().includes(q)) ||
        (item.gender && item.gender.toLowerCase().includes(q)) ||
        (item.hair_color && item.hair_color.toLowerCase().includes(q)) ||
        (item.body_part && item.body_part.toLowerCase().includes(q))
      )
    }
    return result
  }, [items, search, selectedGroup, selectedGender, selectedAge, selectedHairColor, selectedBodyPart])

  const handleSelect = (item: GalleryItem) => {
    onSelect(item)
    onClose()
    setSearch('')
    setSelectedGroup(null)
    setSelectedGender(null)
    setSelectedAge(null)
    setSelectedHairColor(null)
    setSelectedBodyPart(null)
  }

  if (!open) return null

  // FilterRow — horizontal scroll chips
  const FilterRow = ({ label, values, selected, onSelect: onFilterSelect }: {
    label: string
    values: string[]
    selected: string | null
    onSelect: (v: string | null) => void
  }) => (
    <div className="flex items-center gap-1 min-w-0">
      <span className="text-xs text-gray-400 shrink-0 w-[72px]">{label}</span>
      <div className="flex gap-1 overflow-x-auto pb-0.5 scrollbar-hide">
        <button
          onClick={() => onFilterSelect(null)}
          className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs border transition-colors ${
            selected === null
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
          }`}
        >
          Wszystkie
        </button>
        {values.map(val => (
          <button
            key={val}
            onClick={() => onFilterSelect(selected === val ? null : val)}
            className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs border transition-colors ${
              selected === val
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
            }`}
          >
            {val}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <>
      {/* Backdrop — flex centering container */}
      <div
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-2 sm:p-4"
        onClick={onClose}
        aria-hidden="true"
      >
        {/* Modal — centered child, stopPropagation so clicking inside doesn't close */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="flex flex-col bg-white rounded-xl shadow-2xl
                     w-full max-w-5xl h-[92vh] sm:h-[88vh]"
          onClick={e => e.stopPropagation()}
        >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <h6 className="font-semibold text-base text-gray-800">{title}</h6>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Zamknij"
          >
            <CloseIcon fontSize="small" className="text-gray-500" />
          </button>
        </div>

        {/* Search + count */}
        <div className="flex items-center gap-3 px-4 py-2 shrink-0">
          <div className="relative flex-1">
            <SearchIcon
              fontSize="small"
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Szukaj po nazwie, typie, opisie..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg
                         focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200"
            />
          </div>
          <span className="text-xs text-gray-400 shrink-0">{filtered.length} z {items.length}</span>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-1.5 px-4 pb-2 shrink-0">
          {bodyParts.length > 0 && (
            <FilterRow label="Widok:" values={bodyParts} selected={selectedBodyPart} onSelect={setSelectedBodyPart} />
          )}
          {genders.length > 0 && (
            <FilterRow label="Płeć:" values={genders} selected={selectedGender} onSelect={setSelectedGender} />
          )}
          {groups.length > 0 && (
            <FilterRow label={groupLabel} values={groups} selected={selectedGroup} onSelect={setSelectedGroup} />
          )}
          {ages.length > 0 && (
            <FilterRow label="Wiek:" values={ages} selected={selectedAge} onSelect={setSelectedAge} />
          )}
          {hairColors.length > 0 && (
            <FilterRow label="Włosy:" values={hairColors} selected={selectedHairColor} onSelect={setSelectedHairColor} />
          )}
        </div>

        {/* Gallery grid */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-3 pb-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <CircularProgress size={32} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex justify-center py-10">
              <span className="text-sm text-gray-400">
                {search ? 'Brak wyników dla podanego wyszukiwania' : 'Brak obrazów w galerii'}
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-1.5">
              {filtered.map(item => (
                <button
                  key={`${item.id}-${item.age || ''}-${item.ethnicity || ''}`}
                  onClick={() => handleSelect(item)}
                  className="group relative flex flex-col rounded-lg border border-gray-200
                             hover:border-indigo-400 hover:shadow-sm transition-all p-1 text-left
                             focus:outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <img
                    src={item.thumbnail_url || item.image_url}
                    alt={item.name}
                    crossOrigin="anonymous"
                    loading="lazy"
                    style={imageAspectRatio
                      ? { aspectRatio: imageAspectRatio, objectFit: imageObjectFit, width: '100%' }
                      : { height: imageHeight, objectFit: imageObjectFit }
                    }
                    className="w-full rounded bg-gray-100 mb-0.5"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement
                      const full = item.image_url || ''
                      if (full && img.src !== full && !img.src.endsWith(full)) {
                        img.src = full
                      } else {
                        img.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23eee" width="100" height="100"/></svg>'
                      }
                    }}
                  />
                  {/* Wiersz 1: pose_name | figure_type | name + body_part | gender | height */}
                  <span
                    className="block text-xs text-gray-600 font-medium overflow-hidden text-ellipsis whitespace-nowrap w-full"
                    title={item.pose_name || item.figure_type || item.name}
                  >
                    {item.pose_name || item.figure_type || item.name}
                    {item.body_part && <> │ {item.body_part}</>}
                    {!item.body_part && item.gender && <> │ {item.gender}</>}
                    {item.height_range && <> │ {item.height_range}cm</>}
                  </span>
                  {/* Wiersz 2: opis lub gender */}
                  {(item.description || item.gender) && (
                    <span
                      className="block text-[10px] text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap w-full leading-tight"
                      title={item.description || item.gender}
                    >
                      {item.description || (item.body_part && item.gender ? item.gender : '')}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        </div> {/* modal */}
      </div> {/* backdrop */}
    </>
  )
}

export default GalleryModal
